import React from 'react';
import { BadgeProps } from '@frontend/shadcn/components/ui/badge';
import { Cross2Icon } from '@radix-ui/react-icons';
import { Tooltip, TooltipTrigger, TooltipProvider } from '@frontend/shadcn/components/ui/tooltip';
import {
  TooltipHeader,
  TooltipTitle,
  TooltipBody,
  TooltipItem,
  TooltipItemKey,
  TooltipItemValue,
  TooltipContent,
} from '../../../components/tooltip';
import { DismissableBadge } from '@/applications/Social/components/filters/badges/dismissable-badge';
import { getCountryLabel } from '../filters-form/utils';

interface CountryData {
  id: string;
  names: string[]; // Assuming 'names' contains country names or codes
}

interface DemographicsCountriesBadgeProps {
  data: CountryData;
  onDismiss: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
}

type CombinedProps = BadgeProps & DemographicsCountriesBadgeProps;

const DemographicsCountriesBadge = React.forwardRef<HTMLDivElement, CombinedProps>(
  ({ data, onDismiss, ...props }, ref) => {
    if (!data || !data.names || data.names.length === 0) {
      return null;
    }

    // Assuming data.name contains country names directly.
    // If it contains codes that need mapping, a dictionary similar to ageRangeDictionary would be needed.
    const displayNames = data.names.map((country) => getCountryLabel(country));

    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <div>
              <DismissableBadge
                ref={ref}
                key={data ? data.id : 'location-pill'}
                id={data ? data.id : 'location-pill'}
                variant="secondary"
                className="mr-2 mb-2"
                aria-label="Location filter"
                role="button"
                data-dd-action-name="open-location-filters"
                {...props}
              >
                Location:<span className="capitalize ml-1">{displayNames.length > 0 ? displayNames[0] : 'All'}</span>
                {displayNames.length > 1 && <span className="ml-1">+{displayNames.length - 1}</span>}
                {displayNames.length > 0 && (
                  <button
                    type="button"
                    onClick={onDismiss}
                    aria-label="Remove location filter"
                    className="flex ml-1 p-1 focus:outline-none focus:ring"
                    data-dd-action-name="remove-location-filters"
                  >
                    <Cross2Icon className="h-3 w-3" />
                  </button>
                )}
                <span className="sr-only">
                  {displayNames.length > 0 ? 'Remove' : 'Add'} location filter for {displayNames.length > 0 ? displayNames[0] : 'locations'}
                </span>
              </DismissableBadge>
            </div>
          </TooltipTrigger>
          <TooltipContent>
            <TooltipHeader>
              <TooltipTitle>Location Filter</TooltipTitle>
            </TooltipHeader>
            <TooltipBody>
              <TooltipItem>
                <TooltipItemKey>Selected Locations</TooltipItemKey>
                <TooltipItemValue>
                  {displayNames.length > 0 ? displayNames.join(', ') : 'All Locations'}
                </TooltipItemValue>
              </TooltipItem>
            </TooltipBody>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }
);

DemographicsCountriesBadge.displayName = 'DemographicsCountriesBadge';

export default DemographicsCountriesBadge;
